import React from 'react';
import { FormikProps } from 'formik';
import { useAuthorization } from 'hooks/useAuthorization';
import { UserRole } from 'models/user';
import { UseQueryResult } from 'react-query';

import { CurrencyAccountDto } from '@alpha/currency-accounts-dtos';
import { TradeDto } from '@alpha/fx-dtos';

import { MassDrawdownRequestForm } from '../formData';
import FundingMethod from '../FundingMethod';
import MassDrawdownDetails from '../MassDrawdownDetails';
import MassDrawdownTable from '../MassDrawdownTable';
import useMassDrawdown from '../useMassDrawdown';

interface DrawdownCreationProps {
  selectedTrades: TradeDto[];
  requestForm: FormikProps<MassDrawdownRequestForm>;
  handleSplitChange: (
    e: React.ChangeEvent<{ value: string | unknown }>
  ) => void;
  handleCurrencyChange: (
    e: React.ChangeEvent<{ value: string | unknown }>
  ) => void;
  handleAmountChange: (
    amount: number,
    trade: TradeDto
  ) => void;
  massDrawdownValueDate: string | null;
  additionalTrades: TradeDto[];
  handleAddAdditionalTrade: (tradeId: string) => void;
  setConfirmationModalType: (type: 'CLEAR' | 'CLOSE' | 'REMOVE') => void;
  setOpenConfirmation: (open: boolean) => void;
  setTradeIdToRemove: (tradeId: string) => void;
  insufficientFunds: boolean;
  showFunding: () => boolean;
  accountConfigQuery: UseQueryResult<any, Error>;
  defaultSellCurrencyAccount: CurrencyAccountDto;
  tradeErrors: Record<string, string>;
}

const DrawdownCreation: React.FC<DrawdownCreationProps> = ({
  selectedTrades,
  requestForm,
  handleSplitChange,
  handleCurrencyChange,
  handleAmountChange,
  massDrawdownValueDate,
  additionalTrades,
  handleAddAdditionalTrade,
  setConfirmationModalType,
  setOpenConfirmation,
  setTradeIdToRemove,
  insufficientFunds,
  showFunding,
  accountConfigQuery,
  defaultSellCurrencyAccount,
  tradeErrors,
}) => {
  const {
    padEligibleAndApproved,
  } = useMassDrawdown();
  const { authorized: padApprover } = useAuthorization([[UserRole.PAD_APPROVER]]);
  return (

    <>
      <MassDrawdownDetails
        selectedTrades={selectedTrades}
        handleSplitChange={handleSplitChange}
        handleCurrencyChange={handleCurrencyChange}
        handleAmountChange={handleAmountChange}
        requestForm={requestForm}
        massDrawdownValueDate={massDrawdownValueDate ?? ''}
      />
      <MassDrawdownTable
        selectedTrades={selectedTrades}
        handleAmountChange={handleAmountChange}
        requestForm={requestForm}
        handleAddAdditionalTrade={handleAddAdditionalTrade}
        setTradeIdToRemove={setTradeIdToRemove}
        setConfirmationModalType={setConfirmationModalType}
        setOpenConfirmation={setOpenConfirmation}
        additionalTrades={additionalTrades}
        tradeErrors={tradeErrors}
      />

      <div style={{ marginTop: '-9px', marginBottom: '10px' }}>
        {showFunding() && (
        <FundingMethod
          drawdownForm={requestForm}
          padAllowed={
              (accountConfigQuery.data?.padAllowed
                && accountConfigQuery.data?.padEligible
                && padApprover)
              || padEligibleAndApproved
            }
          defaultAccountName={
              defaultSellCurrencyAccount?.friendlyName
              || defaultSellCurrencyAccount?.accountName
              || '-'
            }
          defaultAccountBalance={
              defaultSellCurrencyAccount?.clearedBalance ?? 0
            }
          defaultAccountCurrencyCode={
              defaultSellCurrencyAccount?.currencyCode ?? ''
            }
          insufficientFunds={insufficientFunds}
          show={requestForm.values.totalDrawdownAmount !== 0}
        />
        )}
      </div>
    </>
  );
};

export default DrawdownCreation;
